import React from 'react';
import { useLocation } from 'react-router-dom';
import { Navbar } from '../components/shell/Navbar';
import { Sidebar } from '../components/shell/Sidebar';
import { BottomNav } from '../components/shell/BottomNav';
import { ContextBar } from '../components/shell/ContextBar';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { DemoBanner } from '../components/onboarding/DemoBanner';
import { MaintenanceBanner } from '../components/shell/MaintenanceBanner';
import { OfflineBanner } from '../components/shell/OfflineBanner';
import { IdleTimeoutHandler } from '../components/IdleTimeoutHandler';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isGlobal, colorMode } = useTenant();
  const { isSignedIn } = useCurrentUser();
  const location = useLocation();
  const isDark = colorMode === 'dark';
  
  const isAuthPage = location.pathname.startsWith('/sign-in') || 
                     location.pathname.startsWith('/sign-up');

  const publicRoutes = ['/', '/showcase', '/features', '/plans', '/about', '/privacy', '/use-cases', '/mission', '/newsroom', '/contact', '/terms', '/cookies', '/security', '/dpa', '/aup', '/dmca', '/global', '/compare', '/demo'];
  
  // Normalize path for lookup: remove trailing slash, handle root
  const rawPath = location.pathname.toLowerCase();
  const normalizedPath = rawPath === '/' ? '/' : rawPath.replace(/\/$/, '');

  const isMarketingPage = publicRoutes.includes(normalizedPath) || 
                          normalizedPath.startsWith('/tools') || 
                          normalizedPath.startsWith('/newsroom/') ||
                          normalizedPath.startsWith('/compare/') ||
                          isAuthPage;

  if (isMarketingPage) {
    return (
      <div className="min-h-[100dvh] bg-transparent flex flex-col">
        <OfflineBanner />
        <MaintenanceBanner />
        {children}
      </div>
    );
  }

  // Section 9.2: Global Portal Layout (no sidebar) - ONLY for unauthenticated users or specific public/auth pages
  if ((isGlobal && !isSignedIn) || isAuthPage) {
    return (
      <div className={cn("flex flex-col min-h-[100dvh]", isDark ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-900 dark:text-white dark:bg-slate-950")}>
        <OfflineBanner />
        <MaintenanceBanner />
        {isSignedIn && <IdleTimeoutHandler />}
        {/* We show the Navbar even on auth pages now, but themed for public use */}
        <Navbar />
        <main className={cn(
          "flex-1 w-full pb-24 md:pb-8",
          !isAuthPage && "max-w-4xl mx-auto px-4 md:px-6 py-8",
          isAuthPage && "flex items-center justify-center p-4"
        )}>
          {children}
        </main>
        <BottomNav />
      </div>
    );
  }

  // Section 9.1: Branded Subdomain Shell (With Sidebar)
  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden">
      <OfflineBanner />
      <DemoBanner />
      <MaintenanceBanner />
      {isSignedIn && <IdleTimeoutHandler />}
      <Navbar />
      {isSignedIn && <ContextBar />}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto [scrollbar-gutter:stable] bg-slate-50 px-4 md:px-6 py-6 pb-24 md:pb-6 relative dark:bg-slate-950">
          {children}
        </main>
      </div>
      <BottomNav />
    </div>
  );
};
